import { createMutation } from '@/mutations'
import {
  addWardrobeFromCatalog,
  batchUnlinkWardrobe,
  deleteWardrobeItem,
  patchWardrobeItem,
  uploadWardrobe,
  uploadWardrobeImage,
  type WardrobeBatchUnlinkResponse,
  type WardrobeDeleteResponse,
  type WardrobeFromCatalogResponse,
  type WardrobePatchBody,
  type WardrobePatchResponse,
  type WardrobeUploadResponse,
} from '@/features/style-space/api'
import { styleSpaceQueriesOf } from '@/queries/style-space'

/** A wardrobe write reaches every look and capsule that wears the piece. */
const invalidateWardrobeReaders = styleSpaceQueriesOf(
  'wardrobe',
  'wardrobe-item',
  'outfits',
  'capsules',
  'capsule',
  'available-items',
)

export interface UploadWardrobePhotosVariables {
  files: File[]
}

/** Two hops by contract: each photo → S3 key, then one detection call with all keys (§2.2). */
export const useUploadWardrobePhotosMutation = () =>
  createMutation<WardrobeUploadResponse, UploadWardrobePhotosVariables>({
    mutationFn: async ({ files }) => {
      const keys: string[] = []
      for (const file of files) {
        const uploaded = await uploadWardrobeImage(file)
        keys.push(uploaded.data.img_url)
      }
      const response = await uploadWardrobe(keys)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export interface PatchWardrobeItemVariables {
  overviewId: number
  body: WardrobePatchBody
}

export const usePatchWardrobeItemMutation = () =>
  createMutation<WardrobePatchResponse, PatchWardrobeItemVariables>({
    mutationFn: async ({ overviewId, body }) => {
      const response = await patchWardrobeItem(overviewId, body)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useDeleteWardrobeItemMutation = () =>
  createMutation<WardrobeDeleteResponse, { overviewId: number }>({
    mutationFn: async ({ overviewId }) => {
      const response = await deleteWardrobeItem(overviewId)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useBatchUnlinkWardrobeMutation = () =>
  createMutation<WardrobeBatchUnlinkResponse, { overviewIds: number[] }>({
    mutationFn: async ({ overviewIds }) => {
      const response = await batchUnlinkWardrobe(overviewIds)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

export const useAddWardrobeFromCatalogMutation = () =>
  createMutation<WardrobeFromCatalogResponse, { productId: number; variantId?: number | null }>({
    mutationFn: async ({ productId, variantId }) => {
      const response = await addWardrobeFromCatalog(productId, variantId)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })
